import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Ztlumení ovládacích prvků nad videem po nečinnosti.
 *
 * Záměrně NEskrývá — jen ztlumí na `IDLE_OPACITY`. Původní verze schovávala na
 * `opacity: 0` a k tomu vypínala `pointer-events`, což mělo dvě vady: uživatel
 * nevěděl, že tam ovládání vůbec je, a na dotykovém zařízení musel nejdřív
 * "naslepo" ťuknout do obrazu, aby se objevilo. Ztlumený, ale pořád viditelný
 * a klikatelný prvek obojí řeší — tap na něj je viditelný záměr, ne loterie.
 *
 * `poke()` se volá při jakékoli interakci (pohyb pointeru, tap, klávesa) a odloží
 * ztlumení. `holdOpen` prvky zamkne na plné viditelnosti — používá se během držení
 * PTZ a otevřeného dialogu, kdy by ztlumení pod rukou bylo matoucí.
 *
 * Viditelnost je DERIVOVANÁ (`holdOpen || !idleDimmed`), ne synchronizovaná přes
 * setState v efektu — jinak by každá změna `holdOpen` spustila kaskádu renderů.
 */

/** Opacita v klidu. Dost na to, aby prvek šel najít i trefit, málo na to, aby rušil obraz. */
export const IDLE_OPACITY = 0.2;

export interface AutoHide {
  /** True = uživatel právě interaguje → plná viditelnost. */
  active: boolean;
  /** Hodnota do `style.opacity`: 1 nebo `IDLE_OPACITY`. */
  opacity: number;
  poke: () => void;
}

export function useAutoHide(delayMs = 3_000, holdOpen = false): AutoHide {
  const [idleDimmed, setIdleDimmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const arm = useCallback(
    (ms: number) => {
      clear();
      timer.current = setTimeout(() => {
        timer.current = null;
        setIdleDimmed(true);
      }, ms);
    },
    [clear]
  );

  const poke = useCallback(() => {
    setIdleDimmed(false);
    if (!holdOpen) arm(delayMs);
  }, [arm, delayMs, holdOpen]);

  // Po odemčení `holdOpen` se odpočet nastartuje znovu, ať ovládání nezůstane
  // na plné viditelnosti navždy po skončení pohybu.
  useEffect(() => {
    if (holdOpen) {
      clear();
      return;
    }
    arm(delayMs);
    return clear;
  }, [holdOpen, delayMs, arm, clear]);

  useEffect(() => clear, [clear]);

  const active = holdOpen || !idleDimmed;
  return { active, opacity: active ? 1 : IDLE_OPACITY, poke };
}
